import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { useBooking } from '@/booking';
import { useCurrencyPreview } from '@/currency-preview';
import { useHotel } from '@/hotel';
import { Trans, useTranslation } from '@/i18n';

import { useUpdateBookingAddOn } from '../hooks';
import type { IBookingAddOnInstance } from '../models';
import { AddOnCard } from './AddOnCard.component';

export const AddOnWithCheckbox = (props: {
  addOn: IBookingAddOnInstance;
  bookingId: string;
  hotelReferenceId: string;
  currentAddOns: Pick<IBookingAddOnInstance, 'id' | 'requestedQuantity'>[];
  isLoading: boolean;
  isDisabled: boolean;
  updateAddOn: ReturnType<typeof useUpdateBookingAddOn>['updateAddOn'];
}) => {
  const { t } = useTranslation('additionalInformation');
  const fieldName = `add-on-checkbox` as const;
  const bookingAPIValue = props.currentAddOns.some(
    (a) => a.id === props.addOn.id
  )
    ? props.addOn.id
    : false;
  const { register, watch, setValue } = useForm<{
    [fieldName]: false | string;
  }>({
    defaultValues: {
      [fieldName]: bookingAPIValue,
    },
  });

  const booking = useBooking({
    bookingId: props.bookingId,
    hotelSlug: props.hotelReferenceId,
  });
  const hotel = useHotel(props.hotelReferenceId);

  const value = watch(fieldName);
  const isChecked = Boolean(value);

  const localisedAddOnPrice = useCurrencyPreview({
    amount: props.addOn.cost.amountAfterTax,
  });

  // Sync up booking state if it's different.
  // For instance if add-on was added on a different tab.
  useEffect(() => {
    if (!props.isLoading && bookingAPIValue !== value) {
      setValue(fieldName, bookingAPIValue);
    }
  }, [bookingAPIValue]);

  const descriptionId = `${props.addOn.id}-description`;

  if (!hotel || !booking) return null;

  return (
    <AddOnCard
      isAdded={props.isLoading ? !isChecked : isChecked}
      localisedAddOnPrice={localisedAddOnPrice}
      descriptionId={descriptionId}
      {...props.addOn}
    >
      <AddOnCard.Checkbox
        {...register(fieldName, {
          onChange: async (e: React.ChangeEvent<HTMLInputElement>) => {
            if (props.isLoading || props.isDisabled) {
              /**
               * TODO: field should be disabled beforehand while loading; reverting state like so is ugly, even if it works.
               */
              return setValue(
                fieldName,
                !e.target.checked ? props.addOn.id : false
              );
            }

            await props.updateAddOn(
              {
                id: props.addOn.id,
                // Zero quantity instructs to remove the add-on.
                quantity: e.target.checked ? 1 : 0,
                bookingId: props.bookingId,
                hotelReferenceId: props.hotelReferenceId,
                previousAddOns: props.currentAddOns,
              },
              {
                onSettled: (response) => {
                  // Revert checkbox state back if failed to update add-on
                  if (!response || response.status !== 'CREATED') {
                    setValue(
                      fieldName,
                      !e.target.checked ? props.addOn.id : false
                    );
                  }
                },
              }
            );
          },
        })}
        label={
          <Trans
            defaults={props.addOn.controlLabel}
            values={{ price: localisedAddOnPrice }}
          />
        }
        isChecked={isChecked}
        value={props.addOn.id}
        isLoading={props.isLoading}
        /**
         * Don't ever disable the input as we want to retain focus when add-on is being submitted.
         */
        disabled={false}
        aria-disabled={props.isDisabled || props.isLoading}
        aria-describedby={descriptionId}
        aria-label={
          props.isLoading
            ? isChecked
              ? t('addOns.status.adding', { addOnName: props.addOn.name })
              : t('addOns.status.removing', { addOnName: props.addOn.name })
            : `${props.addOn.name}, ${props.addOn.priceContent}, ${props.addOn.controlLabel}`
        }
      />
    </AddOnCard>
  );
};
